// Adjuntos del chat del Asistente — lo que el usuario "engancha" desde el
// menú "+" antes de mandar un mensaje: una imagen de referencia, un producto
// de su catálogo o un modelo. Aparecen como chips arriba del input y, al
// enviar, se inyectan como contexto para el agente (y, en el caso de la
// imagen, como reference_image en la generación libre).

import { supabase } from '../../lib/supabase';
import { normalizeImageForUpload } from '../../utils/heicConvert';
import { aligerarImagen } from '../../utils/aligerarImagen';
import { firmarEnLote, TTL_FIRMA } from '../../utils/firmarEnLote';
import { getCachedSignedUrl, setCachedSignedUrl } from '../../utils/imageCache';
import type { ModelSelection } from './ModelPicker';

/** El bucket de las referencias que sube el chat; lo comparte el modo Diseño para sus
 *  bases, inspiraciones y texturas (ADR-2026-09-17-las-imagenes-propias-del-diseno-se-suben). */
export const BUCKET_ADJUNTOS_DEL_CHAT = 'video-project-assets';
const BUCKET = BUCKET_ADJUNTOS_DEL_CHAT;
const SIGNED_TTL = 3600;

export type ChatAttachment =
  | {
      kind: 'image';
      id: string;
      /** URL para mostrar la miniatura del chip (firmada). */
      previewUrl: string;
      /** Path en storage — se re-firma al generar para pasarlo como ref. */
      storagePath: string;
      /** Bucket donde vive `storagePath`. Las imágenes subidas van al de
       *  assets; las "ya generadas" viven en `generadas`. Default el de
       *  assets. */
      bucket?: string;
    }
  | {
      kind: 'product';
      id: string;
      /** id real del producto (variante) en el catálogo. */
      productId: string;
      name: string;
      imageUrl: string;
    }
  | {
      kind: 'model';
      id: string;
      /** Selección de modelo (preset / auto / custom / upload). */
      selection: ModelSelection;
      name: string;
      imageUrl: string | null;
    }
  | {
      /**
       * Una REFERENCIA del catálogo de la marca que no es producto ni modelo: una POSE
       * (la postura que tiene que tener la persona) o un FONDO (el escenario) —
       * ADR-2026-09-08-adjuntar-desde-el-catalogo-de-la-marca.
       *
       * No es `image` porque no se sube ni se re-firma por `storagePath`: la URL ya
       * existe (las poses y los fondos de biblioteca son públicos del mismo storage;
       * los fondos guardados vienen firmados). Y no es lo mismo para el agente: una
       * pose se describe como postura y un fondo como escenario, así que viajan
       * ETIQUETADOS en el mensaje además de entrar como referencia de generación.
       */
      kind: 'referencia';
      id: string;
      /** `moodboard`: una foto de una carpeta de referencias de la marca
       *  (ADR-2026-09-13-los-moodboards-entran-a-la-biblioteca-del-agente). Para el
       *  modelo es una referencia de ESTÉTICA (luz, encuadre, paleta, actitud), y al
       *  motor viaja como `adjunto`: ver `tipoDeReferenciaParaElMotor`. */
      tipo: 'pose' | 'fondo' | 'moodboard';
      name: string;
      /** URL directa (pública o ya firmada) — sirve para el chip y como referencia. */
      imageUrl: string;
    }
  | {
      /**
       * Un MOODBOARD entero (la carpeta) — ADR-2026-09-13-el-agente-ve-los-moodboards.
       * Es un PUNTERO, como el lote de fotos: sus fotos no viajan en el mensaje; el
       * agente las mira con `ver_moodboard`, de a diez. Sólo en la biblioteca del chat.
       */
      kind: 'moodboard';
      id: string;
      moodboardId: string;
      name: string;
      cantidadFotos: number;
      /** Una de las fotos de la portada, para el chip. `null` si la carpeta está vacía. */
      imageUrl: string | null;
    }
  | {
      /**
       * Archivo de TEXTO (txt, md, csv, json…) que el usuario enganchó en el Chat —
       * ADR-2026-09-08-el-chat-toma-el-motor-del-asistente. Viaja INLINE como bloque de
       * texto con el nombre adelante: el modelo no tiene herramienta para bajar una
       * URL, así que un path firmado no le serviría. Lo lee `useV2Chat` al armar el
       * mensaje; el asistente nunca lo crea.
       */
      kind: 'texto';
      id: string;
      name: string;
      /** El contenido plano, ya recortado al tope del Chat. */
      text: string;
      /** Peso del contenido, para el chip. */
      bytes: number;
    }
  | {
      /**
       * Una PLANILLA (Excel o CSV) que el usuario enganchó —
       * ADR-2026-09-08-el-agente-lee-y-escribe-planillas.
       *
       * A diferencia del texto, NO viaja entera: 5.000 filas no entran en un turno ni
       * pueden arrastrarse en todo el historial. Viaja el `resumen` (forma + muestra) y
       * el contenido queda en memoria bajo `planillaId`, de donde lo lee `ver_planilla`
       * cuando el agente pide más filas.
       */
      kind: 'planilla';
      id: string;
      name: string;
      /** Clave del registro en memoria (`planillasEnMemoria`). */
      planillaId: string;
      /** Forma + muestra: es lo que se inyecta en el turno. */
      resumen: string;
      /** Para el chip: cuántas hojas y filas. */
      hojas: number;
      filas: number;
    }
  | {
      /**
       * Un PDF — ADR-2026-09-08-el-agente-lee-y-crea-pdfs. Como la planilla, NO viaja
       * entero: viaja el `resumen` y los bytes quedan en memoria bajo `pdfId`, de donde
       * `ver_pdf` saca más páginas (texto, o renderizadas si está escaneado).
       */
      kind: 'pdf';
      id: string;
      name: string;
      pdfId: string;
      resumen: string;
      paginas: number;
      /** false = escaneado: sólo se puede mirar como imagen. */
      tieneTexto: boolean;
    }
  | {
      /**
       * Un VIDEO de referencia — ADR-2026-09-11-el-agente-entiende-videos-con-gemini.
       * Sube al storage como la imagen, pero el modelo NO lo ve (Claude no recibe video):
       * lo entiende con `ver_video`, que se lo manda a Gemini desde el backend. En el
       * mensaje queda el path: como está en el storage y no en memoria, sigue disponible
       * tras una recarga.
       */
      kind: 'video';
      id: string;
      name: string;
      storagePath: string;
      bucket?: string;
      bytes: number;
      duracionSeg: number;
      /** Un cuadro del video (su miniatura), si quien lo adjunta lo tiene: para dibujarlo
       *  donde hace falta una imagen (la tarjeta de referencia de Redes). */
      previewUrl?: string;
    };

/** Sube una imagen de referencia al bucket y devuelve su path + URL firmada
 *  para preview. No inserta fila en DB (adjunto efímero del chat). */
export async function uploadReferenceImage(
  file: File,
  companyId: string,
): Promise<{ storagePath: string; previewUrl: string }> {
  // HEIC → JPEG primero, y después el tope de peso/lado: lo que se sube acá es lo que el
  // modelo va a bajar por URL, y no tiene `opt/` que lo salve
  // (ADR-2026-09-14-el-modelo-ve-el-opt-y-el-error-habla-en-castellano).
  const normalized = await aligerarImagen(await normalizeImageForUpload(file));
  const ext =
    (normalized.name.match(/\.([a-zA-Z0-9]+)$/)?.[1] || 'jpg').toLowerCase();
  const storagePath = `${companyId}/agent-refs/${crypto.randomUUID()}.${ext}`;

  const { error: uploadErr } = await supabase.storage
    .from(BUCKET)
    .upload(storagePath, normalized, {
      contentType: normalized.type || undefined,
      upsert: false,
    });
  if (uploadErr) throw new Error(`No se pudo subir la imagen: ${uploadErr.message}`);

  const signed = await signReferenceImage(storagePath);
  return { storagePath, previewUrl: signed || '' };
}

/**
 * Sube un video de referencia al bucket (carpeta propia, sin fila en DB: adjunto efímero
 * del chat). Sin normalizar: el backend se lo manda a Gemini tal cual.
 */
export async function uploadReferenceVideo(
  file: File,
  companyId: string,
): Promise<{ storagePath: string; bucket: string }> {
  const ext = (file.name.match(/\.([a-zA-Z0-9]+)$/)?.[1] || 'mp4').toLowerCase();
  const storagePath = `${companyId}/agent-videos/${crypto.randomUUID()}.${ext}`;
  const { error: uploadErr } = await supabase.storage
    .from(BUCKET)
    .upload(storagePath, file, { contentType: file.type || 'video/mp4', upsert: false });
  if (uploadErr) throw new Error(`No se pudo subir el video: ${uploadErr.message}`);
  return { storagePath, bucket: BUCKET };
}

/** Firma un path del bucket para pasarlo como reference_image / preview.
 *  `bucket` default el de assets; las imágenes ya generadas usan `generadas`. */
export async function signReferenceImage(
  storagePath: string,
  bucket: string = BUCKET,
): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(storagePath, SIGNED_TTL);
  if (error || !data?.signedUrl) return null;
  return data.signedUrl;
}

export interface ImagenSubida {
  storagePath: string;
  /** Firma del ORIGINAL: es la que viaja en el adjunto y la que `leerFirma` renueva. */
  previewUrl: string;
  /** Miniatura con transformación, sólo para dibujar en grillas
   *  (ADR-2026-09-25-revision-greptile-la-miniatura-no-viaja-en-el-adjunto). */
  miniaturaUrl?: string;
}

/**
 * Las imágenes que el usuario ya subió al chat (las de `agent-refs`), más nuevas
 * primero y firmadas para la miniatura. Es la pestaña "Subidas" de la biblioteca del
 * Agente (ADR-2026-09-12-la-biblioteca-de-adjuntos-en-la-pantalla-del-agente): volver a
 * engancharlas no las sube de nuevo, el path ya existe. Sólo imágenes: los videos
 * también viven ahí y no van en esa grilla.
 */
export async function listarImagenesSubidas(
  companyId: string,
  limite = 60,
): Promise<ImagenSubida[]> {
  return (await listarImagenesSubidasPagina(companyId, limite)).imagenes;
}

/**
 * Lo mismo que `listarImagenesSubidas`, más si la carpeta DEVOLVIÓ el tope entero
 * (`vinoLlena`). La lista de Storage trae también videos y otros archivos, que acá se
 * descartan: si "¿hay más?" se midiera sobre las imágenes que quedaron, bastaría un video
 * entre las primeras 60 para cortar el paginado y dejar inaccesibles las más viejas
 * (ADR-2026-09-25-revision-greptile-el-paginado-se-mide-sobre-lo-que-trajo-la-base).
 */
export async function listarImagenesSubidasPagina(
  companyId: string,
  limite = 60,
): Promise<{ imagenes: ImagenSubida[]; vinoLlena: boolean }> {
  const carpeta = `${companyId}/agent-refs`;
  const { data, error } = await supabase.storage.from(BUCKET).list(carpeta, {
    limit: limite,
    sortBy: { column: 'created_at', order: 'desc' },
  });
  if (error || !data) return { imagenes: [], vinoLlena: false };
  const vinoLlena = data.length >= limite;
  const paths = data
    .filter((f) => /\.(jpe?g|png|webp|gif)$/i.test(f.name))
    .map((f) => `${carpeta}/${f.name}`);
  // Dos firmas por archivo: el ORIGINAL (en lote) para el adjunto, y la miniatura para la
  // grilla —lo que sube la gente puede ser una foto de cámara de varios MB y ahí es un tile
  // de 170 px (ADR-2026-09-25-el-selector-de-fuentes-carga-miniaturas-y-por-scroll)—. La
  // miniatura NO viaja en el adjunto: `leerFirma` no renueva firmas con transformación
  // (ADR-2026-09-25-revision-greptile-la-miniatura-no-viaja-en-el-adjunto).
  const [originales, miniaturas] = await Promise.all([
    firmarEnLote(paths, BUCKET),
    firmarMiniaturas(paths, BUCKET),
  ]);
  const imagenes = paths.flatMap((storagePath) => {
    const previewUrl = originales.get(storagePath);
    if (!previewUrl) return [];
    const miniaturaUrl = miniaturas.get(storagePath);
    return [{ storagePath, previewUrl, ...(miniaturaUrl ? { miniaturaUrl } : {}) }];
  });
  return { imagenes, vinoLlena };
}

/** Caja de la miniatura de una subida: la misma de los fondos y las poses (tiles de
 *  140–220 px @2x, `contain` para no recortar). */
const MINIATURA = { width: 440, height: 782, resize: 'contain' as const, quality: 75 };
const FIRMAS_A_LA_VEZ = 10;

async function firmarMiniaturas(paths: string[], bucket: string): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  const pendientes: string[] = [];
  for (const path of paths) {
    const clave = `${path}#miniatura`;
    const cacheada = getCachedSignedUrl(clave);
    if (cacheada) out.set(path, cacheada);
    else pendientes.push(path);
  }
  for (let i = 0; i < pendientes.length; i += FIRMAS_A_LA_VEZ) {
    const tanda = pendientes.slice(i, i + FIRMAS_A_LA_VEZ);
    const firmadas = await Promise.all(
      tanda.map(async (path) => {
        const { data } = await supabase.storage
          .from(bucket)
          .createSignedUrl(path, TTL_FIRMA, { transform: MINIATURA });
        return [path, data?.signedUrl ?? null] as const;
      }),
    );
    for (const [path, url] of firmadas) {
      if (!url) continue;
      out.set(path, url);
      setCachedSignedUrl(`${path}#miniatura`, url);
    }
  }
  return out;
}
